// Cognito auth backend — used in production. aws-amplify v6 owns the session (USER_SRP_AUTH, no
// client secret); this bridge adapts it to the app's uniform AppAuth surface. The API receives the
// Cognito ACCESS token (not the id token) as the bearer — it pins `client_id` to this app's SPA
// client and maps it to the cc-patient scope.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Amplify } from "aws-amplify";
import {
  fetchAuthSession,
  fetchUserAttributes,
  signOut as amplifySignOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { COGNITO_CLIENT_ID, COGNITO_USER_POOL_ID } from "../config";
import { AuthContext, type AppAuth } from "./context";

let configured = false;
function configureAmplify() {
  if (configured) return;
  configured = true;
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: COGNITO_USER_POOL_ID,
        userPoolClientId: COGNITO_CLIENT_ID,
      },
    },
  });
}

interface UserInfo {
  email: string | null;
  givenName: string | null;
  familyName: string | null;
}

export function CognitoAuthProvider({ children }: { children: ReactNode }) {
  configureAmplify();

  const [isLoaded, setIsLoaded] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);

  // Resolve the current session (amplify persists tokens in localStorage and silently refreshes via
  // the refresh-token flow). Any failure resolves to signed-out — never throws.
  const refresh = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const signedIn = Boolean(session.tokens?.accessToken);
      setIsSignedIn(signedIn);
      if (signedIn) {
        try {
          // The patient SPA client's read attributes are email/email_verified/custom:tier
          // (terraform/cognito) — given_name/family_name are not exposed, so they resolve null and
          // displayName falls back to email. The app backend (PUT /v1/patient/me) owns names.
          const attrs = await fetchUserAttributes();
          setUser({
            email: attrs.email ?? null,
            givenName: attrs.given_name ?? null,
            familyName: attrs.family_name ?? null,
          });
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch {
      setIsSignedIn(false);
      setUser(null);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Hub relays auth lifecycle events (signedIn from the custom sign-in form, signedOut,
    // tokenRefresh) so the context stays reactive without polling.
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
        case "signedOut":
        case "tokenRefresh":
        case "tokenRefresh_failure":
          void refresh();
          break;
      }
    });
    return unsubscribe;
  }, [refresh]);

  const value = useMemo<AppAuth>(
    () => ({
      isLoaded,
      isSignedIn,
      displayName: user?.givenName ?? user?.email ?? null,
      // Treat any failure (offline, expired refresh token) as "no token" so the API client falls
      // through to unauthenticated rather than crashing.
      getToken: async () => {
        try {
          const session = await fetchAuthSession();
          return session.tokens?.accessToken?.toString() ?? null;
        } catch {
          return null;
        }
      },
      signOut: async () => {
        await amplifySignOut();
      },
      // No-op: the pool's SPA client cannot write name attributes (write_attributes is
      // email + custom:tier only — terraform/cognito/main.tf), so there is no auth-side name to
      // mirror. The backend PUT /v1/patient/me is the sole name source; the Account page already
      // treats updateProfile as best-effort.
      updateProfile: async () => {},
    }),
    [isLoaded, isSignedIn, user, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
